/**
 * Accessible infinite carousel for partner logos
 * - Keyboard accessible (Tab, Arrow keys)
 * - ARIA live region for announcements
 * - Infinite loop (clones for seamless scroll)
 * - Pause on hover/focus
 * - Responsive, touch support
 * Usage: Call initCarousel(document.querySelector('.logo-carousel')) after DOMContentLoaded
 *
 * @param {HTMLElement} root - Carousel root element.
 */
export function initCarousel(root) {
  if (!(root instanceof HTMLElement) || root.dataset.logoCarouselInitialized === 'true') {
    return;
  }

  const track = root.querySelector('.logo-carousel__track');
  if (!track) return;

  const items = Array.from(track.children);
  if (!items.length) return;

  root.dataset.logoCarouselInitialized = 'true';

  const live = root.querySelector('.logo-carousel__live');
  let intervalId = null;
  let isPaused = false;

  /**
   * Applies accessibility attributes to cloned items so hidden clones are not focusable.
   *
   * @param {HTMLElement} clone - Cloned carousel item.
   * @returns {void}
   */
  function applyCloneA11y(clone) {
    // aria-hidden hides clones from screen readers.
    // We intentionally do NOT set `inert` here — that would block mouse clicks on cloned logos.
    // tabindex="-1" on focusable descendants is enough to remove them from keyboard tab order.
    clone.setAttribute('aria-hidden', 'true');

    const focusableSelectors = 'a, button, input, select, textarea, [tabindex]';
    const focusableElements = clone.querySelectorAll(focusableSelectors);

    focusableElements.forEach((element) => {
      element.setAttribute('tabindex', '-1');
    });
  }

  /**
   * Returns the configured gap between items from CSS.
   *
   * @returns {number}
   */
  function getItemGap() {
    const styles = window.getComputedStyle(track);
    const gap = parseFloat(styles.columnGap || styles.gap || '0');
    return Number.isNaN(gap) ? 0 : gap;
  }

  /**
   * Returns the horizontal scroll distance for one carousel step.
   *
   * @returns {number}
   */
  function getStepSize() {
    return items[0].offsetWidth + getItemGap();
  }

  // Clone items for infinite effect
  items.forEach(item => {
    const clone = item.cloneNode(true);
    applyCloneA11y(clone);
    track.appendChild(clone);
  });

  // --- Ensure enough items to fill the carousel width ---
  /**
   * Clones enough items to fill the visible carousel width.
   *
   * @returns {void}
   */
  function fillToViewport() {
    const trackWidth = track.offsetWidth;
    const itemWidth = getStepSize();
    const minItems = Math.ceil(trackWidth / itemWidth) + 1;
    let currentCount = items.length;

    // If not enough, clone until filled
    while (currentCount < minItems) {
      items.forEach(item => {
        if (currentCount >= minItems) return;
        const clone = item.cloneNode(true);
        applyCloneA11y(clone);
        track.appendChild(clone);
        currentCount++;
      });
    }
  }
  // Wait for images to load, then fill
  if (items[0] && items[0].querySelector('img')) {
    const imgs = Array.from(track.querySelectorAll('img'));
    let loaded = 0;
    imgs.forEach(img => {
      if (img.complete) loaded++;
      else img.addEventListener('load', () => {
        loaded++;
        if (loaded === imgs.length) fillToViewport();
      });
    });
    if (loaded === imgs.length) fillToViewport();
  } else {
    fillToViewport();
  }

  // Scroll logic
  /**
   * Advances the carousel forward by one item.
   *
   * @returns {void}
   */
  function scrollNext() {
    if (isPaused) return;
    track.scrollBy({ left: getStepSize(), behavior: 'smooth' });

    // If at end, reset
    if (track.scrollLeft >= track.scrollWidth / 2) {
      track.scrollLeft = 0;
    }
  }

  // Auto-scroll
  /**
   * Starts auto-scroll if it is not already running.
   *
   * @returns {void}
   */
  function start() {
    if (intervalId !== null) return;
    intervalId = window.setInterval(scrollNext, 2000);
  }

  /**
   * Stops the active auto-scroll interval.
   *
   * @returns {void}
   */
  function stop() {
    if (intervalId === null) return;
    window.clearInterval(intervalId);
    intervalId = null;
  }

  // Pause on hover/focus
  root.addEventListener('mouseenter', () => { isPaused = true; stop(); });
  root.addEventListener('mouseleave', () => { isPaused = false; start(); });
  root.addEventListener('focusin', () => { isPaused = true; stop(); });
  root.addEventListener('focusout', () => { isPaused = false; start(); });

  // Keyboard navigation
  root.addEventListener('keydown', e => {
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      scrollNext();
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      track.scrollBy({ left: -getStepSize(), behavior: 'smooth' });
      if (track.scrollLeft <= 0) {
        track.scrollLeft = track.scrollWidth / 2;
      }
    }
  });

  // Touch support
  let startX = 0;
  track.addEventListener('touchstart', e => {
    startX = e.touches[0].clientX;
    stop();
  });
  track.addEventListener('touchend', () => {
    isPaused = false;
    start();
  });
  track.addEventListener('touchmove', e => {
    const dx = e.touches[0].clientX - startX;
    track.scrollLeft -= dx;
    startX = e.touches[0].clientX;
  });

  // ARIA live region for announcements
  if (live) {
    live.setAttribute('aria-live', 'polite');
    live.textContent = 'Carousel: Use left/right arrows to scroll logos.';
  }

  // Set tabindex for accessibility
  root.setAttribute('tabindex', '0');

  // Start auto-scroll
  start();
}
